import { supabase } from '@/src/supabaseClient';
import { logger } from '@shared/logging/logger';
import { auditLogger } from '../audit/auditLogger';

export interface SessionConfig {
  inactivityTimeoutMs: number; // e.g. 20 mins
  warningTimeoutMs: number;    // e.g. 15 mins
}

export type SessionEvent = 
  | { type: 'SESSION_LOGOUT'; source: string }
  | { type: 'SESSION_ACTIVE'; timestamp: number }
  | { type: 'SESSION_REFRESH_REQUIRED' };

class SessionManager {
  private config: SessionConfig = {
    inactivityTimeoutMs: 20 * 60 * 1000, // 20 minutes
    warningTimeoutMs: 15 * 60 * 1000,    // 15 minutes
  };

  private initialized: boolean = false;
  private lastActiveTimestamp: number = Date.now();
  private channel: BroadcastChannel | null = null;
  private inactivityTimer: any = null;
  private warningTimer: any = null;
  private onTimeoutCallback: (() => void) | null = null;
  private onWarningCallback: ((remainingMs: number) => void) | null = null;
  private onKeepAliveCallback: (() => void) | null = null;

  constructor() {
    this.setupBroadcastChannel();
    this.setupInactivityListeners();
  }

  /**
   * Initializes session parameters and starts checking inactivity routines.
   */
  public initialize(
    config?: Partial<SessionConfig>,
    onTimeout?: () => void,
    onWarning?: (remainingMs: number) => void,
    onKeepAlive?: () => void
  ): void {
    if (config) {
      this.config = { ...this.config, ...config };
    }
    this.onTimeoutCallback = onTimeout || null;
    this.onWarningCallback = onWarning || null;
    this.onKeepAliveCallback = onKeepAlive || null;

    this.lastActiveTimestamp = Date.now();
    this.initialized = true;

    this.resetTimer();
    logger.debug('security', 'SessionManager initialized with configured thresholds.');
  }

  /**
   * Registers global mouse/keyboard activity tracking listeners.
   */
  private setupInactivityListeners(): void {
    if (typeof window === 'undefined') return;

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const throttleInterval = 2000; // Throttle activity signals to every 2s
    let lastRecorded = 0;

    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastRecorded > throttleInterval) {
        lastRecorded = now;
        this.trackActivity();
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });
  }

  /**
   * Updates activity timestamps and broadcasts telemetry metadata.
   */
  public trackActivity(): void {
    if (!this.initialized) return;

    this.lastActiveTimestamp = Date.now();
    this.resetTimer();
    
    // Notify sibling tabs of activity to keep session alive there too
    this.broadcast({ type: 'SESSION_ACTIVE', timestamp: this.lastActiveTimestamp });
    
    if (this.onKeepAliveCallback) {
      this.onKeepAliveCallback();
    }
  }

  /**
   * Resets active timeout and warning schedules.
   */
  private resetTimer(): void {
    if (!this.initialized || typeof window === 'undefined') return;

    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);

    const now = Date.now();
    const elapsed = now - this.lastActiveTimestamp;

    // Remaining warning time
    const warningRemaining = Math.max(0, this.config.warningTimeoutMs - elapsed);
    // Remaining absolute timeout
    const absoluteRemaining = Math.max(0, this.config.inactivityTimeoutMs - elapsed);

    // Schedule Warning
    this.warningTimer = setTimeout(() => {
      if (!this.initialized) return;
      const remaining = this.config.inactivityTimeoutMs - this.config.warningTimeoutMs;
      logger.warn('security', `Session warning triggered. Inactivity timeout close. Idle for ${this.config.warningTimeoutMs / 1000}s`);
      if (this.onWarningCallback) {
        this.onWarningCallback(remaining);
      }
    }, warningRemaining);

    // Schedule Absolute Logout
    this.inactivityTimer = setTimeout(() => {
      if (!this.initialized) return;
      logger.warn('security', `Session timeout reached after inactivity period (${this.config.inactivityTimeoutMs / 1000}s). Processing sign-out.`);
      this.handleLogout('inactivity_protection');
    }, absoluteRemaining);
  }

  /**
   * Handles multi-tab secure sign-out. Emits event, logs audit metadata, and clear sessions.
   */
  public async handleLogout(reason: string = 'user_action'): Promise<void> {
    if (!this.initialized && reason === 'inactivity_protection') return;
    
    this.initialized = false;
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);

    try {
      const sessionData = await supabase.auth.getSession();
      const user = sessionData.data.session?.user || null;

      // Register Audit Trace before signed out
      await auditLogger.log({
        userId: user?.id || null,
        userEmail: user?.email || null,
        role: null,
        action: `Session Logout: ${reason}`,
        category: 'session',
        severity: 'low',
        status: 'success',
        metadata: { reason }
      });

      // Terminate connection on cloud Supabase Auth
      await supabase.auth.signOut();

      // Emit tab-wide logout command
      this.broadcast({ type: 'SESSION_LOGOUT', source: reason });

      if (this.onTimeoutCallback) {
        this.onTimeoutCallback();
      }
    } catch (err) {
      logger.error('security', 'Critical error during session termination routine:', err);
    } finally {
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    }
  }

  /**
   * Safe initialization of horizontal communication Bus (BroadcastChannel).
   */
  private setupBroadcastChannel(): void {
    if (typeof window === 'undefined') return;

    try {
      this.channel = new BroadcastChannel('wastebank_security_session');
      this.channel.onmessage = (event: MessageEvent<SessionEvent>) => {
        this.handleBroadcastMessage(event.data);
      };
    } catch (err) {
      logger.warn('security', 'BroadcastChannel not available. Tab synchronization limited.', err);
    }
  }

  /**
   * Processes remote security inputs from duplicate or parallel window cards.
   */
  private handleBroadcastMessage(msg: SessionEvent): void {
    switch (msg.type) {
      case 'SESSION_LOGOUT':
        logger.warn('security', `Sibling tab signaled force logout. Source: ${msg.source}`);
        if (this.onTimeoutCallback) {
          this.onTimeoutCallback();
        }
        break;
      
      case 'SESSION_ACTIVE':
        // Synchronize idle clock with sibling activity indicators
        this.lastActiveTimestamp = msg.timestamp;
        this.resetTimer();
        break;

      case 'SESSION_REFRESH_REQUIRED':
        logger.info('security', 'Sibling tab demanded session refreshing checks.');
        supabase.auth.getSession().then(); // Triggers refresh pipeline inside client
        break;
    }
  }

  private broadcast(payload: SessionEvent): void {
    if (this.channel) {
      try {
        this.channel.postMessage(payload);
      } catch (err) {
        logger.debug('security', 'Error sending session bus broadcast:', err);
      }
    }
  }

  /**
   * Public interface to signal token refreshing demands from Auth context.
   */
  public triggerForceRefreshNotification(): void {
    this.broadcast({ type: 'SESSION_REFRESH_REQUIRED' });
  }

  /**
   * teardown listeners and communication channels (helpful in React hook updates).
   */
  public destroy(): void {
    this.initialized = false;
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);
    this.onTimeoutCallback = null;
    this.onWarningCallback = null;
    this.onKeepAliveCallback = null;
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
  }
}

export const sessionManager = new SessionManager();
export default sessionManager;
